import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useDeleteCustomerAddress from '@/api/mutations/customers/useDeleteCustomerAddress';
import customerKeys from '@/api/queries/customers/keyFactory';
import {
  CustomerQuery,
  DeleteCustomerAddressInput,
  DeleteCustomerAddressMutation,
} from '@/gql/graphql';

const useOptimisticDeleteCustomerAddress = (
  customerId: string,
  options?: UseMutationOptions<
    DeleteCustomerAddressMutation,
    unknown,
    DeleteCustomerAddressInput
  >
) => {
  const queryClient = useQueryClient();

  return useDeleteCustomerAddress({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: customerKeys.customer(customerId),
      });

      // Snapshot the previous value
      const previousCustomer = queryClient.getQueryData(
        customerKeys.customer(customerId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerQuery>(
        customerKeys.customer(customerId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            customer: {
              ...old.customer,
              addresses: old.customer.addresses.filter(
                (address) => address.id !== variables.addressId
              ),
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomer };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        customerKeys.customer(customerId),
        // @ts-expect-error
        context.previousCustomer
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
      queryClient.invalidateQueries({
        queryKey: customerKeys.customer(customerId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticDeleteCustomerAddress;
